/**
 * Client for the Super Admin identity & security modules (apps/api/src/routes/platform-security.ts)
 * and the self-service 2FA / impersonation endpoints (apps/api/src/routes/auth.ts).
 * Every call is authorised on the server; nothing here grants access by itself.
 */
import { api } from '@/lib/api';
import type { SessionContext } from '@/lib/types';

export interface SettingSnapshot<T> {
  value: T;
  version: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const securityKeys = {
  all: ['platform', 'security'] as const,
  overview: ['platform', 'security', 'overview'] as const,
  users: ['platform', 'security', 'users'] as const,
  impersonationPolicy: ['platform', 'security', 'impersonation-policy'] as const,
  candidates: (q: string) => ['platform', 'security', 'candidates', q] as const,
  impersonations: ['platform', 'security', 'impersonations'] as const,
  google: ['platform', 'security', 'google'] as const,
  twitter: ['platform', 'security', 'twitter'] as const,
  ipRules: ['platform', 'security', 'ip-rules'] as const,
  myIp: ['platform', 'security', 'my-ip'] as const,
  ipSettings: ['platform', 'security', 'ip-settings'] as const,
  maintenance: ['platform', 'security', 'maintenance'] as const,
  maintenanceHistory: ['platform', 'security', 'maintenance-history'] as const,
  loginAlerts: ['platform', 'security', 'login-alerts'] as const,
  loginAlertLogs: ['platform', 'security', 'login-alert-logs'] as const,
  loginEvents: ['platform', 'security', 'login-events'] as const,
  loginAlertsSummary: ['platform', 'security', 'login-alerts-summary'] as const,
  twoFactorStatus: ['me', 'two-factor'] as const,
};

async function del<T>(path: string, body: unknown): Promise<T> {
  const r = await api.raw<{ data: T }>(path, { method: 'DELETE', body });
  return r.data;
}

/* ---------------------------------------------------------------- impersonation */

export interface ImpersonationPolicy {
  enabled: boolean;
  maxMinutes: number;
  requireTenantSupportGrant: boolean;
}

export interface ImpersonationCandidate {
  id: string;
  name: string;
  email: string;
  lastLoginAt: string | null;
  lastActiveAt: string | null;
  contexts: SessionContext[];
}

export type ImpersonationStatus = 'active' | 'exit' | 'forced' | 'logout' | 'expired' | 'revoked';

export interface ImpersonationRow {
  id: string;
  operator: { id: string; name: string; email: string };
  target: { id: string; name: string; email: string };
  tenant: { id: string; name: string | null } | null;
  contextKind: 'staff' | 'client' | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  status: ImpersonationStatus;
  endedBy: { id: string; name: string; email: string } | null;
  forceEndReason: string | null;
}

export interface StartImpersonationInput {
  userId: string;
  reason: string;
  tenantId?: string;
  contextKind?: 'staff' | 'client';
  membershipId?: string;
  participantId?: string;
}

/* ---------------------------------------------------------------- 2FA & sessions */

export interface SecurityOverview {
  twoFactorPolicy: SettingSnapshot<{ requireForOperators: boolean; requireForTenantAdmins: boolean }>;
  sessionPolicy: SettingSnapshot<{ idleMinutes: number; absoluteHours: number }> & {
    bounds: { idleMinutes: { min: number; max: number }; absoluteHours: { min: number; max: number } };
    envDefaults: { idleMinutes: number; absoluteHours: number };
  };
  impersonationPolicy: SettingSnapshot<ImpersonationPolicy>;
  passwordPolicy: { minLength: number; maxLength: number; rejectsRepeatedSingleCharacter: boolean; hashing: string; lockout: { threshold: number; minutes: number }; resetLinkMinutes: number };
  stats: {
    activeUsers: number;
    activeUsersWithTwoFactor: number;
    operators: number;
    operatorsWithTwoFactor: number;
    tenantAdmins: number;
    tenantAdminsWithTwoFactor: number;
    activeSessions: number;
    activeImpersonations: number;
  };
}

export interface SecurityUserRow {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'disabled';
  platformRole: string | null;
  twoFactor: { enabled: boolean; enrolledAt: string | null; pendingSetup: boolean };
  googleLinked: boolean;
  /** Linked sign-in providers (e.g. google, twitter). Optional for older API builds. */
  authProviders?: string[];
  hasPassword?: boolean;
  /** Temporarily locked after repeated failed sign-ins. */
  locked?: boolean;
  activeSessions?: number;
  lastLoginAt: string | null;
  lastActiveAt: string | null;
}

/** Account actions reused from the platform users module (routes/platform-users.ts); all audited, reason required. */
export type SecurityUserAction = 'revoke-sessions' | 'unlock' | 'password-reset' | 'disable' | 'enable';

export interface TwoFactorStatus {
  enabled: boolean;
  enrolledAt: string | null;
  pendingSetup: boolean;
  recoveryCodesRemaining: number;
  required: boolean;
}

/* ---------------------------------------------------------------- Google */

export interface GoogleLoginValue {
  enabled: boolean;
  clientId: string;
  allowedDomains: string[];
  autoLinkByVerifiedEmail: boolean;
  allowSignup: boolean;
}

export interface GoogleLoginView {
  value: GoogleLoginValue;
  version: number;
  updatedAt: string | null;
  clientSecret: { masked: string; fingerprint: string; rotatedAt: string | null; createdAt: string } | null;
  redirectUri: string;
  linkedAccounts: number;
  publicEnabled: boolean;
  status: SocialProviderStatus;
  lastTest: SocialLastTest | null;
}

/**
 * Server-derived provider status: `connected` only after a passing test of the exact saved
 * configuration; `configured` when saved but untested, failed, or changed since the last test.
 */
export type SocialProviderStatus = 'setup_required' | 'disabled' | 'configured' | 'connected';

export interface SocialLastTest {
  testedAt: string;
  status: 'passed' | 'failed';
  error: string | null;
  /** False once the client ID or secret changed after this test. */
  current: boolean;
}

export interface GoogleTestResult {
  ok: boolean;
  checks: Array<{ key: string; ok: boolean; message: string }>;
  testedAt: string;
}

/* ---------------------------------------------------------------- X (Twitter) */

export interface TwitterLoginValue {
  enabled: boolean;
  clientId: string;
  autoLinkByVerifiedEmail: boolean;
  allowSignup: boolean;
}

export interface TwitterLoginView {
  value: TwitterLoginValue;
  version: number;
  updatedAt: string | null;
  clientSecret: { masked: string; fingerprint: string; rotatedAt: string | null; createdAt: string } | null;
  redirectUri: string;
  scopes: string[];
  linkedAccounts: number;
  publicEnabled: boolean;
  status: SocialProviderStatus;
  lastTest: SocialLastTest | null;
}

/* ---------------------------------------------------------------- IP blocker */

export type IpBlockerMode = 'blocklist' | 'allowlist_operators';

export interface IpRuleRow {
  id: string;
  type: 'block' | 'allow';
  cidr: string;
  version: 4 | 6;
  scope: 'platform' | 'tenant';
  tenant: { id: string; name: string | null } | null;
  reason: string;
  enabled: boolean;
  expiresAt: string | null;
  expired: boolean;
  hitCount: number;
  lastHitAt: string | null;
  createdBy: { id: string; name: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlockerState {
  active: boolean;
  killSwitch: boolean;
  enabled: boolean;
  mode: IpBlockerMode;
}

export interface MyIp {
  ip: string;
  version: 4 | 6 | null;
  blocker: BlockerState;
  decision: { blocked: boolean; blockedByRuleId: string | null; allowedByRuleId: string | null };
  operatorAllowlisted: boolean;
  breakGlass: boolean;
  breakGlassConfigured: boolean;
  trustProxy: boolean;
}

export interface IpCheckResult {
  ip: string;
  blocked: boolean;
  effective: boolean;
  blocker: BlockerState;
  blockedBy: string | null;
  allowedBy: string | null;
  matches: Array<IpRuleRow & { considered: boolean }>;
}

export interface CreateIpRuleInput {
  type: 'block' | 'allow';
  cidr: string;
  scope: 'platform' | 'tenant';
  tenantId?: string;
  reason: string;
  expiresAt?: string | null;
  enabled?: boolean;
  addAllowForMyIp?: boolean;
}

/* ---------------------------------------------------------------- maintenance */

export interface MaintenanceValue {
  enabled: boolean;
  message: string;
  startsAt: string | null;
  endsAt: string | null;
  allowOperators: true;
  allowedTenantIds: string[];
}

export interface MaintenanceView extends SettingSnapshot<MaintenanceValue> {
  state: { active: boolean; scheduled: boolean };
  confirmPhrase: string;
  openWindowId: string | null;
}

export interface MaintenanceHistoryRow {
  id: string;
  message: string;
  reason: string;
  disableReason: string | null;
  enabledBy: { id: string; name: string; email: string } | null;
  disabledBy: { id: string; name: string; email: string } | null;
  startedAt: string;
  endedAt: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  status: 'active' | 'scheduled' | 'ended' | 'ended_on_schedule';
}

/* ---------------------------------------------------------------- login alerts */

export interface LoginAlertsValue {
  enabled: boolean;
  notifyOnNewDevice: boolean;
  notifyOnEveryLogin: boolean;
  includeIp: boolean;
  subject: string;
  bodyText: string;
  videoUrl: string;
  notifyOnSuspicious: boolean;
  recipientGroups: LoginAlertGroup[];
}

export type LoginAlertGroup = 'operators' | 'organisation' | 'clients';

export interface LoginAlertLogRow {
  id: string;
  to: string;
  subject: string;
  status: string;
  error: string | null;
  provider: string | null;
  attempts: number;
  sentAt: string | null;
  createdAt: string;
  test: boolean;
  newDevice: boolean | null;
  suspicious: boolean;
  method: string | null;
}

export interface LoginEventRow {
  id: string;
  at: string;
  outcome: 'success' | 'failed' | 'locked' | 'mfa_failed';
  user: { id: string; name: string; email: string; operator: boolean; twoFactorEnabled: boolean } | null;
  method: string | null;
  mfa: boolean | null;
  ip: string | null;
  device: string;
  alert: { status: string; error?: string | null; suspicious?: boolean; newDevice?: boolean | null } | null;
}

export interface LoginAlertsSummary {
  last24h: { successfulSignIns: number; failedSignIns: number; alertsSent: number; alertsFailed: number; suspicious: number };
  rule: { failedAttempts: number; windowMinutes: number };
}

/* ---------------------------------------------------------------- calls */

export const securityApi = {
  impersonationPolicy: (signal?: AbortSignal) => api.get<SettingSnapshot<ImpersonationPolicy>>('/platform/impersonation/policy', undefined, { signal }),
  candidates: (q: string, signal?: AbortSignal) => api.get<ImpersonationCandidate[]>('/platform/impersonation/candidates', { q }, { signal }),
  startImpersonation: (body: StartImpersonationInput) => api.post<{ impersonationId: string; target: { id: string; name: string; email: string }; expiresAt: string; redirectTo: string }>('/platform/impersonation', body),
  endImpersonation: (id: string, reason: string) => api.post<{ id: string }>(`/platform/impersonation/sessions/${id}/end`, { reason }),
  exitImpersonation: () => api.post<{ restored: boolean; redirectTo: string }>('/auth/impersonation/exit', {}, { silent401: true }),

  overview: (signal?: AbortSignal) => api.get<SecurityOverview>('/platform/security', undefined, { signal }),
  saveTwoFactorPolicy: (value: SecurityOverview['twoFactorPolicy']['value'], expectedVersion: number) => api.put<SettingSnapshot<SecurityOverview['twoFactorPolicy']['value']>>('/platform/security/two-factor', { value, expectedVersion }),
  saveSessionPolicy: (value: SecurityOverview['sessionPolicy']['value'], expectedVersion: number) => api.put<SettingSnapshot<SecurityOverview['sessionPolicy']['value']>>('/platform/security/sessions', { value, expectedVersion }),
  saveImpersonationPolicy: (value: ImpersonationPolicy, expectedVersion: number) => api.put<SettingSnapshot<ImpersonationPolicy>>('/platform/security/impersonation', { value, expectedVersion }),
  resetUserTwoFactor: (userId: string, reason: string) => api.post<{ reset: boolean; sessionsRevoked: number }>(`/platform/security/users/${userId}/reset-2fa`, { reason }),
  userAction: (userId: string, action: SecurityUserAction, reason: string) =>
    api.post<{ sessionsRevoked?: number; wasLocked?: boolean; changed?: boolean; email?: { status: 'sent' | 'failed' | 'queued'; error: string | null } }>(`/platform/users/${encodeURIComponent(userId)}/${action}`, { reason }),

  google: (signal?: AbortSignal) => api.get<GoogleLoginView>('/platform/google-login', undefined, { signal }),
  saveGoogle: (body: GoogleLoginValue & { clientSecret?: string | null; expectedVersion?: number }) => api.put<GoogleLoginView>('/platform/google-login', body),
  testGoogle: () => api.post<GoogleTestResult>('/platform/google-login/test', {}),
  twitter: (signal?: AbortSignal) => api.get<TwitterLoginView>('/platform/twitter-login', undefined, { signal }),
  saveTwitter: (body: TwitterLoginValue & { clientSecret?: string | null; expectedVersion?: number }) => api.put<TwitterLoginView>('/platform/twitter-login', body),
  testTwitter: () => api.post<GoogleTestResult>('/platform/twitter-login/test', {}),

  myIp: (signal?: AbortSignal) => api.get<MyIp>('/platform/ip-rules/my-ip', undefined, { signal }),
  checkIp: (ip: string) => api.post<IpCheckResult>('/platform/ip-rules/check', { ip }),
  createIpRule: (body: CreateIpRuleInput) => api.post<{ rule: IpRuleRow; allowRule: IpRuleRow | null }>('/platform/ip-rules', body),
  updateIpRule: (id: string, body: { enabled?: boolean; reason?: string; expiresAt?: string | null }) => api.patch<IpRuleRow>(`/platform/ip-rules/${id}`, body),
  deleteIpRule: (id: string, reason: string) => del<{ deleted: boolean }>(`/platform/ip-rules/${id}`, { reason }),
  ipSettings: (signal?: AbortSignal) => api.get<SettingSnapshot<{ enabled: boolean; mode: IpBlockerMode }> & { killSwitchActive: boolean; breakGlassConfigured: boolean }>('/platform/ip-blocker/settings', undefined, { signal }),
  saveIpSettings: (value: { enabled: boolean; mode: IpBlockerMode }, expectedVersion: number) => api.put<SettingSnapshot<{ enabled: boolean; mode: IpBlockerMode }>>('/platform/ip-blocker/settings', { value, expectedVersion }),

  maintenance: (signal?: AbortSignal) => api.get<MaintenanceView>('/platform/maintenance', undefined, { signal }),
  enableMaintenance: (body: { confirm: string; reason: string; message: string; startsAt: string | null; endsAt: string | null; allowedTenantIds: string[]; expectedVersion?: number }) =>
    api.post<MaintenanceView>('/platform/maintenance/enable', body),
  disableMaintenance: (reason: string) => api.post<MaintenanceView>('/platform/maintenance/disable', { reason }),

  loginAlerts: (signal?: AbortSignal) => api.get<SettingSnapshot<LoginAlertsValue>>('/platform/login-alerts', undefined, { signal }),
  saveLoginAlerts: (value: LoginAlertsValue, expectedVersion: number) => api.put<SettingSnapshot<LoginAlertsValue>>('/platform/login-alerts', { value, expectedVersion }),
  previewLoginAlert: (value: LoginAlertsValue, signal?: AbortSignal) => api.post<{ subject: string; text: string }>('/platform/login-alerts/preview', { value }, { signal }),
  testLoginAlert: (value: LoginAlertsValue) => api.post<{ status: 'sent' | 'failed'; to: string; error: string | null; logId: string }>('/platform/login-alerts/test', { value }),
  loginAlertsSummary: (signal?: AbortSignal) => api.get<LoginAlertsSummary>('/platform/login-alerts/summary', undefined, { signal }),

  twoFactorStatus: (signal?: AbortSignal) => api.get<TwoFactorStatus>('/auth/2fa/status', undefined, { signal }),
  twoFactorSetup: () => api.post<{ secret: string; otpauthUri: string; issuer: string; account: string }>('/auth/2fa/setup', {}),
  twoFactorEnable: (code: string) => api.post<{ enabled: true; recoveryCodes: string[] }>('/auth/2fa/enable', { code }),
  twoFactorDisable: (password: string, code: string) => api.post<{ enabled: false }>('/auth/2fa/disable', { password, code }),
  regenerateRecoveryCodes: (code: string) => api.post<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes/regenerate', { code }),
};

/* ---------------------------------------------------------------- helpers */

/** ISO → value for <input type="datetime-local"> in the viewer's timezone. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO (null when empty/invalid). */
export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Loose client-side check; the server's CIDR parser is authoritative. */
export function looksLikeIpOrCidr(v: string): boolean {
  const s = v.trim();
  const [addr, prefix, extra] = s.split('/');
  if (!addr || extra !== undefined) return false;
  if (prefix !== undefined && !/^\d{1,3}$/.test(prefix)) return false;
  const v4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/.test(addr);
  if (v4) return prefix === undefined || Number(prefix) <= 32;
  const v6 = /^[0-9a-fA-F:.]+$/.test(addr) && addr.includes(':');
  return v6 && (prefix === undefined || Number(prefix) <= 128);
}

export function copyText(text: string): Promise<void> {
  return navigator.clipboard?.writeText ? navigator.clipboard.writeText(text) : Promise.reject(new Error('Clipboard unavailable'));
}
